<script lang="ts">
  import type { Capabilities, Conversation } from '../../shared/types';
  import { del, get, post } from '../api';
  import { chat } from '../store';
  import Icon from './Icon.svelte';

  interface SettingsView {
    meshName: string;
    telegramToken: boolean;
    discordToken: boolean;
    livekitUrl: string;
    livekitApiKey: boolean;
    livekitApiSecret: boolean;
  }

  type Status = { kind: 'idle' | 'busy' | 'ok' | 'error'; text?: string };

  const EMPTY_VIEW: SettingsView = {
    meshName: '',
    telegramToken: false,
    discordToken: false,
    livekitUrl: '',
    livekitApiKey: false,
    livekitApiSecret: false,
  };

  const open = $derived($chat.settingsOpen);
  const me = $derived($chat.me);
  const capabilities = $derived($chat.capabilities);
  const peers = $derived($chat.peers);

  let view = $state<SettingsView>(EMPTY_VIEW);
  let form = $state<Record<string, string>>({});
  let nameDraft = $state(me.name);
  let status = $state<Status>({ kind: 'idle' });
  let telegramToken = $state('');
  let discordToken = $state('');
  let chatPlatform = $state<'telegram' | 'discord'>('telegram');
  let chatId = $state('');
  let roomName = $state('');
  let invite = $state('');

  $effect(() => {
    if (!open) return;
    get<{ settings: SettingsView }>('/api/settings')
      .then((result) => {
        view = result.settings;
        form = {};
        nameDraft = chat.getState().me.name;
      })
      .catch((error: Error) => (status = { kind: 'error', text: error.message }));
  });

  const field = (key: string) => form[key] ?? '';

  function saveName() {
    const name = nameDraft.trim();
    if (!name) {
      status = { kind: 'error', text: 'Enter a display name first' };
      return;
    }
    chat.setMeName(name);
    status = { kind: 'ok', text: 'Name saved' };
  }

  async function save() {
    status = { kind: 'busy', text: 'Saving…' };
    const patch: Record<string, string> = {};
    for (const key of ['meshName', 'livekitUrl']) {
      if (key in form) patch[key] = form[key];
    }
    for (const key of ['livekitApiKey', 'livekitApiSecret']) {
      if (field(key).trim()) patch[key] = form[key];
    }
    try {
      const result = await post<{ settings: SettingsView }>('/api/settings', patch);
      view = result.settings;
      form = {};
      status = { kind: 'ok', text: 'Saved' };
    } catch (error) {
      status = { kind: 'error', text: (error as Error).message };
    }
  }

  async function connect(platform: 'telegram' | 'discord') {
    const token = platform === 'telegram' ? telegramToken.trim() : discordToken.trim();
    if (!token) return;
    status = { kind: 'busy', text: `Connecting ${platform}…` };
    try {
      await post(`/api/integrations/${platform}`, { token });
      if (platform === 'telegram') telegramToken = '';
      else discordToken = '';
      status = { kind: 'ok', text: `${platform} connected` };
    } catch (error) {
      status = { kind: 'error', text: (error as Error).message };
    }
  }

  async function disconnect(platform: 'telegram' | 'discord') {
    status = { kind: 'busy', text: `Disconnecting ${platform}…` };
    try {
      await del(`/api/integrations/${platform}`);
      status = { kind: 'ok', text: `${platform} disconnected` };
    } catch (error) {
      status = { kind: 'error', text: (error as Error).message };
    }
  }

  async function addChat() {
    if (!chatId.trim()) return;
    status = { kind: 'busy', text: 'Opening chat…' };
    try {
      const result = await post<{ conversation: Conversation }>('/api/chats', {
        platform: chatPlatform,
        id: chatId.trim(),
      });
      chat.upsertConversation(result.conversation);
      chatId = '';
      status = { kind: 'ok', text: `Added ${result.conversation.title}` };
    } catch (error) {
      status = { kind: 'error', text: (error as Error).message };
    }
  }

  async function createRoom() {
    status = { kind: 'busy', text: 'Creating mesh room…' };
    try {
      const result = await post<{ conversation: Conversation }>('/api/mesh/rooms', {
        name: roomName.trim() || 'Mesh room',
      });
      chat.upsertConversation(result.conversation);
      roomName = '';
      status = { kind: 'ok', text: `Room ${result.conversation.title} created` };
    } catch (error) {
      status = { kind: 'error', text: (error as Error).message };
    }
  }

  async function joinRoom() {
    if (!invite.trim()) return;
    status = { kind: 'busy', text: 'Joining mesh…' };
    try {
      const result = await post<{ conversation: Conversation }>('/api/mesh/join', { invite: invite.trim() });
      chat.upsertConversation(result.conversation);
      invite = '';
      status = { kind: 'ok', text: `Joined ${result.conversation.title}` };
    } catch (error) {
      status = { kind: 'error', text: (error as Error).message };
    }
  }

  const caps: Capabilities | null = capabilities;
</script>

{#if open}
  <div
    class="modal-backdrop"
    role="presentation"
    onclick={() => chat.toggleSettings(false)}
  >
    <div class="modal" role="dialog" aria-modal="true" aria-label="Settings" onclick={(event) => event.stopPropagation()}>
      <header class="modal-head">
        <strong>Settings</strong>
        <button class="icon-btn" onclick={() => chat.toggleSettings(false)} aria-label="Close">
          <Icon name="close" />
        </button>
      </header>

      <div class="modal-body">
        <section class="card">
          <h3>You</h3>
          <label>
            Display name
            <div class="row">
              <input
                bind:value={nameDraft}
                aria-label="Display name"
                onkeydown={(event) => {
                  if (event.key === 'Enter') saveName();
                }}
              />
              <button class="btn" onclick={saveName} disabled={!nameDraft.trim()}>Save name</button>
            </div>
          </label>
          <p class="hint">Shown on the messages you send. Saved in this browser.</p>
          <label>
            Mesh peer name
            <input
              value={field('meshName') || view.meshName}
              oninput={(event) => (form = { ...form, meshName: event.currentTarget.value })}
              placeholder="Chatsi peer"
            />
          </label>
          <div class="peers">
            <span class={caps?.mesh.enabled ? 'dot ok' : 'dot'}></span>
            {caps?.mesh.enabled ? `Peer ${caps.mesh.peerId} · ${caps.mesh.peers} connected` : 'Mesh offline'}
            {#if peers.filter((peer) => !peer.self).length}
              <span class="peer-names">{peers.filter((peer) => !peer.self).map((peer) => peer.name).join(', ')}</span>
            {/if}
          </div>
        </section>

        <section class="card">
          <h3>
            Telegram <span class={caps?.telegram.enabled ? 'tag ok' : 'tag'}>{caps?.telegram.enabled ? 'connected' : 'off'}</span>
          </h3>
          <p class="hint">
            Bot token from <code>@BotFather</code>. Chats appear once the bot receives a message, or add one by id below.
          </p>
          <div class="row">
            <input
              type="password"
              bind:value={telegramToken}
              placeholder={view.telegramToken ? 'Token saved — enter a new one to replace' : '123456:ABC-DEF…'}
            />
            <button class="btn" onclick={() => void connect('telegram')} disabled={!telegramToken.trim()}>Connect</button>
            <button class="btn ghost" onclick={() => void disconnect('telegram')} disabled={!view.telegramToken}>Remove</button>
          </div>
          {#if caps?.telegram.detail}<p class="hint">{caps.telegram.detail}</p>{/if}
        </section>

        <section class="card">
          <h3>
            Discord <span class={caps?.discord.enabled ? 'tag ok' : 'tag'}>{caps?.discord.enabled ? 'connected' : 'off'}</span>
          </h3>
          <p class="hint">Bot token, with the Message Content intent enabled in the Discord developer portal.</p>
          <div class="row">
            <input
              type="password"
              bind:value={discordToken}
              placeholder={view.discordToken ? 'Token saved — enter a new one to replace' : 'Bot token'}
            />
            <button class="btn" onclick={() => void connect('discord')} disabled={!discordToken.trim()}>Connect</button>
            <button class="btn ghost" onclick={() => void disconnect('discord')} disabled={!view.discordToken}>Remove</button>
          </div>
          {#if caps?.discord.detail}<p class="hint">{caps.discord.detail}</p>{/if}
        </section>

        <section class="card">
          <h3>Open a chat</h3>
          <div class="row">
            <select bind:value={chatPlatform}>
              <option value="telegram">Telegram</option>
              <option value="discord">Discord</option>
            </select>
            <input
              bind:value={chatId}
              placeholder={chatPlatform === 'telegram' ? '@channel or chat id' : 'Discord channel id'}
            />
            <button class="btn" onclick={() => void addChat()} disabled={!chatId.trim()}>Open</button>
          </div>
        </section>

        <section class="card">
          <h3>Pear mesh</h3>
          <p class="hint">Rooms replicate messages peer-to-peer over Hyperswarm. Share the invite code to let a peer join.</p>
          <div class="row">
            <input bind:value={roomName} placeholder="Room name" />
            <button class="btn" onclick={() => void createRoom()}>Create</button>
          </div>
          <div class="row">
            <input bind:value={invite} placeholder="Invite code" />
            <button class="btn" onclick={() => void joinRoom()} disabled={!invite.trim()}>Join</button>
          </div>
        </section>

        <section class="card">
          <h3>LiveKit calls</h3>
          <div class="row">
            <input
              value={field('livekitUrl') || view.livekitUrl}
              oninput={(event) => (form = { ...form, livekitUrl: event.currentTarget.value })}
              placeholder="wss://your.livekit.cloud"
            />
          </div>
          <div class="row">
            <input
              type="password"
              value={field('livekitApiKey')}
              oninput={(event) => (form = { ...form, livekitApiKey: event.currentTarget.value })}
              placeholder={view.livekitApiKey ? 'API key saved' : 'API key'}
            />
            <input
              type="password"
              value={field('livekitApiSecret')}
              oninput={(event) => (form = { ...form, livekitApiSecret: event.currentTarget.value })}
              placeholder={view.livekitApiSecret ? 'API secret saved' : 'API secret'}
            />
          </div>
          <p class="hint">
            Calls use this LiveKit project. Audio, video and screen sharing stay within the call participants.
          </p>
        </section>
      </div>

      <footer class="modal-foot">
        <span class={`status ${status.kind}`}>{status.text}</span>
        <button class="btn" onclick={() => void save()} disabled={status.kind === 'busy'}>Save changes</button>
        <button class="btn ghost" onclick={() => chat.toggleSettings(false)}>Close</button>
      </footer>
    </div>
  </div>
{/if}
